import React, { useEffect, useState } from "react";
import {
	CCButton,
	CCField,
	CCModal,
	CCSpinner,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EventReviewState } from "../../hooks/useEventReview";
import { formatCardDate } from "../../utils/eventDates";
import {
	ReviewIssue,
	ReviewSeverity,
	ShownEventReview,
} from "../../types/event";
import { ORDER, SEVERITY, severityOf } from "./eventReviewSeverity";
import "./eventManager.css";

/**
 * Most severe first. STABLE, so within one severity the issues keep the
 * server's order, which is the AI's ranking of them: this groups by severity
 * without throwing that ranking away.
 */
const bySeverity = (issues: ReviewIssue[]): ReviewIssue[] =>
	issues
		.map((issue, at) => ({ issue, at }))
		.sort(
			(a, b) =>
				ORDER.indexOf(severityOf(a.issue.Severity)) -
					ORDER.indexOf(severityOf(b.issue.Severity)) || a.at - b.at,
		)
		.map(({ issue }) => issue);

const SeverityIcon: React.FC<{ severity: ReviewSeverity }> = ({ severity }) => (
	<FontAwesomeIcon
		icon={SEVERITY[severity].icon}
		className={`em-review__icon em-review__icon--${severity}`}
		/* Named, because in the list the icon IS the severity: nothing else
		   on the row says it. */
		role="img"
		aria-label={SEVERITY[severity].label}
		title={SEVERITY[severity].label}
	/>
);

/** Matches the API's `MAX_QUESTION_CHARS`; longer is a 422. */
const MAX_QUESTION = 500;

interface EventReviewModalProps {
	eventId: string;
	/**
	 * Held by the editor, not here, so the header's "N issues found" link
	 * reads the same result and opens the same modal.
	 */
	review: EventReviewState;
	/**
	 * What to show: this session's check if there is one, otherwise the one
	 * stored on the event. Chosen by the editor, which also feeds the sidebar.
	 */
	result: ShownEventReview | null;
	/** The check was run on an earlier version of the event than the saved one. */
	stale: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The review reads the STORED event, so an unsaved draft cannot be rechecked. */
	dirty: boolean;
	/**
	 * Bumped by the editor after each successful save. A new value runs the
	 * check against what was just stored, without anyone asking for it.
	 */
	checkAfterSave?: number;
}

/** "Mon 28 Sep 2026 at 2:14 pm", for the line saying when the list was
    produced. The date as well as the time: a long editing session can reopen
    a check run on another day. The date is the event cards' own format, so
    the two read alike. */
const checkedAt = (iso: string): string | null => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	const time = d
		.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })
		// "2:14 pm" on some engines and "2:14 PM" on others; see eventDates.
		.toLowerCase();
	return `${formatCardDate(d)} at ${time}`;
};

/**
 * "Check this event": a pre-flight review of the saved event.
 *
 * NO BUTTON OF ITS OWN. There was one at the top right of the editor, on the
 * breadcrumb row; it went once saving started running the check. What is left
 * is the check itself and the modal that shows it: saving a change runs it
 * (see `checkAfterSave`), the header's "N issues found" card
 * opens the modal, and "Check again" in the modal is the explicit rerun, with
 * the question. A list of issues is several paragraphs long, which is why the
 * detail is a modal and not the sidebar: in a 260px column it pushed the
 * sign-off out of view.
 *
 * It reads what is STORED, not the draft on screen, so it follows the sign-off
 * panel's rule: a dirty draft disables "Check again" rather than letting
 * someone check a version they are no longer looking at.
 *
 * ISSUES ARE GROUPED BY SEVERITY, Important first, so the list reads in the
 * same order as the key above it. Within a severity they keep the order the
 * server sent, which is its own priority ranking (see `bySeverity`).
 */
export const EventReviewModal: React.FC<EventReviewModalProps> = ({
	eventId,
	dirty,
	checkAfterSave = 0,
	review,
	result,
	stale,
	open,
	onOpenChange: setOpen,
}) => {
	const { run, running, error, reset } = review;
	const toast = useToast();
	const [question, setQuestion] = useState("");

	/* A different event is a different check. A new save of THIS one does not
	   clear anything: every check carries the version it read
	   (`EventUpdatedAt`), so an old one is marked out of date instead of
	   vanishing. */
	useEffect(() => reset(), [eventId, reset]);

	/* SAVING RUNS THE CHECK, quietly. Issues do NOT open the modal: they
	   show as the "N issues found" link in the editor header, which opens
	   it when the person is ready. A clean result is a toast, since there is
	   no link to show for it. One review per save sits well inside the API's
	   10 a minute. */
	useEffect(() => {
		if (!checkAfterSave) return;
		run(undefined, (review) => {
			if (review.Issues.length === 0) {
				toast.show({
					message: "Event checked: no issues found.",
					severity: "success",
					duration: 3000,
				});
			}
		});
		// Only a new save should start a check, not a re-render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkAfterSave]);

	const when = result ? checkedAt(result.GeneratedAt) : null;
	/* "Unknown" was once written as a name by the actor lookup; see originOf
	   on why it is never shown. */
	const by =
		result?.ReviewedBy?.Name && result.ReviewedBy.Name !== "Unknown"
			? result.ReviewedBy.Name
			: null;

	return (
		<CCModal
			open={open}
			onClose={() => setOpen(false)}
			title="Check this event"
			className="em-review-modal"
			footer={
				<>
					<CCButton variant="outline" onClick={() => setOpen(false)}>
						Close
					</CCButton>
					<CCButton
						variant="solid"
						colour="primary"
						disabled={dirty || running}
						onClick={() => run(question)}
					>
						{running ? (
							<CCSpinner size="inline" label="Checking..." />
						) : (
							"Check again"
						)}
					</CCButton>
				</>
			}
		>
			<div className="em-review">
				{running && !result && (
					<div className="em-review__waiting" role="status">
						<CCSpinner size="md" label="Checking this event..." />
						<span className="em-muted">
							This can take up to half a minute.
						</span>
					</div>
				)}

				{error && (
					<span className="em-error" role="alert">
						{error}
					</span>
				)}

				{result && (
					<div className="em-review__result" aria-live="polite">
						{/* Above everything, because it qualifies everything: the
						    issues below may already be fixed, or joined by new ones. */}
						{stale && !running && (
							<p className="em-review__stale">
								This check was run before the event&rsquo;s latest changes.
								Check again to see where it stands now.
							</p>
						)}
						{/* The count first, above even the answer to a question, so
						    the size of the job is the first thing read. The legend
						    under the answer breaks it down by severity. */}
						<p
							className={
								result.Issues.length === 0
									? "em-review__count em-review__count--clear"
									: "em-review__count"
							}
						>
							{result.Issues.length === 0
								? "No issues found"
								: `${result.Issues.length} ${
										result.Issues.length === 1 ? "issue" : "issues"
									} found`}
						</p>

						{result.Answer && (
							<p className="em-review__answer">{result.Answer}</p>
						)}

						{result.Issues.length > 0 && (
							<>
								{/* Only the severities this list uses, each with its
								    count: a key entry for an icon that appears nowhere
								    below is noise. */}
								<ul className="em-review__legend" aria-label="Key">
									{ORDER.map((sev) => ({
										sev,
										n: result.Issues.filter(
											(i) => severityOf(i.Severity) === sev,
										).length,
									}))
										.filter(({ n }) => n > 0)
										.map(({ sev, n }) => (
											<li key={sev}>
												<FontAwesomeIcon
													icon={SEVERITY[sev].icon}
													className={`em-review__icon em-review__icon--${sev}`}
													aria-hidden="true"
												/>
												<span className="em-review__legend-count">{n}</span>
												{SEVERITY[sev].label}
											</li>
										))}
								</ul>

								<ul className="em-review__list">
									{bySeverity(result.Issues).map((issue, i) => (
										<li
											key={`${issue.Code}-${i}`}
											className="em-review__issue"
										>
											<SeverityIcon severity={severityOf(issue.Severity)} />
											<div className="em-review__issue-body">
												<strong>{issue.Title}</strong>
												<p>{issue.Detail}</p>
												{issue.Suggestion && (
													<p className="em-review__solution">
														<span className="em-review__solution-label">
															Solution
														</span>
														{issue.Suggestion}
													</p>
												)}
											</div>
										</li>
									))}
								</ul>
							</>
						)}

						{result.Facts?.AttendeesTruncated && (
							<p className="em-muted">
								This event has a very large roll, so the student counts cover
								only part of it.
							</p>
						)}
						{!result.AiUsed && (
							<p className="em-muted">
								The assistant was unavailable, so these are the standard
								checks.
							</p>
						)}
						{/* Attendee changes save as they happen and do not move the
						    event's `UpdatedAt`, so they do not mark this out of date.
						    The time says how old it is instead. */}
						{when && (
							<p className="em-muted">
								Checked on {when}
								{by ? ` by ${by}` : ""}.
							</p>
						)}
					</div>
				)}

				{/* Below the results, beside "Check again": asking something is
				    a reason to run it again, not a step before the first run. */}
				<CCField
					id="em-review-question"
					label="Ask something specific (optional)"
					fullWidth
					className="em-review__question"
					value={question}
					maxLength={MAX_QUESTION}
					disabled={running}
					onChange={(e) => setQuestion(e.target.value)}
				/>
			</div>
		</CCModal>
	);
};
